import { Rider } from '../api/rest-client';

/**
 * Type for objects that can be formatted for display
 * Includes Rider and LeaderboardEntry types
 */
export type RiderLike = {
    name: string;
    type?: 'Coach' | 'Rider' | 'Student' | string;
};

/**
 * Format rider name for display, adding "Coach" prefix for coaches
 * @param rider - Rider-like object with name and type properties
 * @returns Formatted display name
 */
export function formatRiderName(rider: RiderLike | Rider | null | undefined): string {
    if (!rider || !rider.name) return '';
    
    const name = rider.name.trim();
    
    // Return empty string if name is only whitespace
    if (!name) return '';
    
    const isCoach = rider.type === 'Coach';
    
    // If already has "Coach" prefix, return as-is (backward compatibility)
    if (name.startsWith('Coach ')) {
        return name;
    }
    
    // Add "Coach" prefix for coaches
    return isCoach ? `Coach ${name}` : name;
}

/**
 * Get badge text for rider type
 * @param rider - Rider-like object with type property
 * @returns Badge text or empty string
 */
export function getRiderBadgeText(rider: RiderLike | Rider | null | undefined): string {
    if (!rider || rider.type !== 'Coach') return '';
    return 'Coach';
}

/**
 * Get badge color class for rider type
 * @param rider - Rider-like object with type property
 * @returns Tailwind CSS color class
 */
export function getRiderBadgeColor(rider: RiderLike | Rider | null | undefined): string {
    if (!rider || rider.type !== 'Coach') return '';
    return 'bg-blue-500 text-white';
}

/**
 * Get rider type badge info (text + color) for Coach or Student
 */
export function getRiderTypeBadge(rider: RiderLike | Rider | null | undefined): { text: string; color: string } {
    if (!rider) return { text: 'Student', color: 'bg-green-500' };
    if (rider.type === 'Coach') return { text: 'Coach', color: 'bg-blue-500' };
    return { text: 'Student', color: 'bg-green-500' };
}

/**
 * Get status badge color (active/inactive/graduated)
 */
export function getStatusBadgeColor(status: string | undefined): string {
    switch (status) {
        case 'active':
            return 'bg-green-500';
        case 'inactive':
            return 'bg-gray-500';
        case 'graduated':
            return 'bg-purple-500';
        default:
            return 'bg-gray-500';
    }
}

/**
 * Get user role badge color
 */
export function getUserRoleBadgeColor(groups: string[]): { text: string; color: string } {
    if (groups.includes('admin')) return { text: 'Admin', color: 'bg-red-500 text-white' };
    if (groups.includes('coach')) return { text: 'Coach', color: 'bg-blue-500 text-white' };
    return { text: 'User', color: 'bg-gray-200 text-gray-600' };
}
